import { Skeleton } from "@/components/ui/skeleton";
import {
  Table,
  TableBody,
  TableCell,
  TableHead,
  TableHeader,
  TableRow,
} from "@/components/ui/table";
import { formatCurrency } from "@/utils/format/currency";
import { LicenseFeeTableProps } from "./list-table-types";
import { getLicenseFeeStatus } from "@/utils/get-license-fee-status";
import { safeFormatInTimeZone } from "@/utils/format/safe-format-in-time-zone";
import {
  Menubar,
  MenubarContent,
  MenubarItem,
  MenubarMenu,
  MenubarSeparator,
  MenubarTrigger,
} from "@/components/ui/menubar";
import { EllipsisHorizontalIcon, TrashIcon } from "@heroicons/react/20/solid";
import { Outlet, useNavigate } from "react-router-dom";
import { EditIcon } from "lucide-react";

export const LicenseFeeTable = ({ data, isLoading }: LicenseFeeTableProps) => {
  const navigate = useNavigate();

  const tableHeaders = [
    {
      name: "ID",
      columnName: "id",
    },
    {
      name: "Empresa",
      columnName: "companyName",
    },
    {
      name: "Data de início",
      columnName: "startDate",
    },
    {
      name: "Data de fim",
      columnName: "endDate",
    },
    {
      name: "Status",
      columnName: "status",
    },
    {
      name: "Valor em R$",
      columnName: "amount",
    },
  ];

  return (
    <div>
      <Outlet />
      <Table>
        <TableHeader>
          <TableRow>
            {tableHeaders.map((header, index) => (
              <TableHead
                key={header.columnName}
                className={index === 0 ? "px-10" : ""}
              >
                {header.name}
              </TableHead>
            ))}
            <TableHead className="w-[50px]" />
          </TableRow>
        </TableHeader>
        <TableBody>
          {isLoading ? (
            <>
              {Array.from({ length: 10 }).map((_, i) => (
                <TableRow key={i}>
                  <TableCell
                    colSpan={tableHeaders.length + 3}
                    className="px-10"
                  >
                    <Skeleton className="w-full h-[40px] rounded-full" />
                  </TableCell>
                </TableRow>
              ))}
            </>
          ) : (
            data?.map((item) => (
              <TableRow key={item.id}>
                <TableCell className="px-10">{item.id}</TableCell>
                <TableCell className="font-medium py-5">
                  {item.companyName}
                </TableCell>
                <TableCell>
                  {safeFormatInTimeZone(item.startDate, "UTC", "dd/MM/yyyy")}
                </TableCell>
                <TableCell>
                  {safeFormatInTimeZone(item.endDate, "UTC", "dd/MM/yyyy")}
                </TableCell>
                <TableCell>{getLicenseFeeStatus(item.status)}</TableCell>
                <TableCell>{formatCurrency(item.amount)}</TableCell>
                <TableCell>
                  <Menubar className="border-none shadow-none relative">
                    <MenubarMenu>
                      <MenubarTrigger className="cursor-pointer">
                        <EllipsisHorizontalIcon width={20} />
                      </MenubarTrigger>
                      <MenubarContent>
                        <MenubarItem
                          onClick={() =>
                            navigate(`/contracts/license-fee/edit/${item.id}`)
                          }
                        >
                          <EditIcon className="mr-2" width={16} />
                          Editar
                        </MenubarItem>

                        {!["cancelled"].includes(item.status || "") &&
                          item.isAbleToCancel && (
                            <>
                              <MenubarSeparator />
                              <MenubarItem
                                className="text-red-600"
                                onClick={() => navigate(`${item.id}/cancel`)}
                              >
                                <TrashIcon
                                  className="text-red-600 hover:text-red-400   mr-2"
                                  width={16}
                                />
                                Cancelar
                              </MenubarItem>
                            </>
                          )}
                      </MenubarContent>
                    </MenubarMenu>
                  </Menubar>
                </TableCell>
              </TableRow>
            ))
          )}
          {data?.length === 0 && (
            <TableRow>
              <TableCell colSpan={tableHeaders.length + 3}>
                <p className="font-bold mt-5 text-center text-lg">
                  Não foi encontrado nenhum license fee.
                </p>
              </TableCell>
            </TableRow>
          )}
        </TableBody>
      </Table>
    </div>
  );
};
